import { config } from '../../config';
import type { HypothesisRow } from '../../types';
import { planLockPositions } from '../board';
import { canLock, MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import type { DomainResult } from '../result';
import {
  isCalendarDate,
  isFormationFor,
  reconcileFormationGuess,
  reconcileHalfLength,
} from '../setup';
import type {
  BoardOp,
  CreatePlayerCommand,
  CreatePlayerSnapshot,
  CreateTeamCommand,
  CreateTeamSnapshot,
  Decision,
  InstallSeedTemplatesCommand,
  InstallSeedTemplatesSnapshot,
  MatchSetupPatch,
  SetupOp,
  StartCaptureCommand,
  StartCaptureSnapshot,
  UpdateMatchSetupCommand,
  UpdateMatchSetupSnapshot,
} from './types';

/**
 * PRE-1 … PRE-7 — the Pre-Match write path, as pure handlers (build unit U-7).
 *
 * ── Why these are commands and not repo calls from a screen ──────────────────
 *
 * U-4's inheritance note is unambiguous: *"`dispatch` is the only mutation path. A screen that
 * writes through a repo directly is a defect."* `Team`, `Player` and `Hypothesis` rows are domain
 * data — they are walked by INV-8's cascade, carried in SYN-4's archive, and read by FILE-1 and
 * FILE-2 — so they arrive through the same one path as everything else. The exception U-4 *does*
 * carry (`resume.recordSurface` writing `UiSession` outside the bus) is a UI hint that no archive
 * ever sees; nothing here is.
 *
 * ── The one shape worth reading twice: `handleStartCapture` ──────────────────
 *
 * PRE-7's two exits differ only in whether positions are assigned, and design §6.0 spends a
 * paragraph on why that matters: *"Positions are therefore written by exactly one of the two paths,
 * never both."* Starting in Discovery assigns **nothing**, so a later LOCK-4 confirm is the first and
 * only assignment and INV-4's "exactly once per lock" holds across the two units together. Starting
 * locked routes through the same `planLockPositions` that LOCK-4 uses, so §6.2's two table rows
 * ("LOCK-4 confirm" and "PRE-7 start locked") are literally one code path rather than two that must
 * be kept agreeing.
 */

const MODULE = 'commands/setup';

// ─────────────────────────────────────────────────────────────────────────────
// PRE-1 — Team
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRE-1 — "Create or select opponent Team".
 *
 * The only rule is a name: FILE-1 lists Teams by name and PRE-5 offers a *previously scouted Team*,
 * so a nameless row is a row the user can never find again. There is deliberately no uniqueness
 * rule — two clubs really do field teams with the same name, and the adversarial fixture carries
 * colliding team names on purpose.
 */
export function handleCreateTeam(
  cmd: CreateTeamCommand,
  _snap: CreateTeamSnapshot,
): Decision<SetupOp> {
  if (cmd.row.name.trim() === '') {
    return reject(
      MODULE,
      null,
      'ERR_TEAM_NAME_REQUIRED',
      'Give the opponent a name so you can find them again.',
      { id: cmd.row.id },
    );
  }

  return ok({
    ops: [{ t: 'insertTeam', row: cmd.row }],
    log: [
      { module: MODULE, level: 'info', matchId: null, msg: 'team created', data: { id: cmd.row.id } },
    ],
    post: [],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-2 — the seeded library
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRE-2 — installs the built-in library, inserting only what is missing.
 *
 * *"Seeded built-in template library … the app ships with ≥ 10 hypotheses per moment bucket. Seed
 * content is data (JSON), not code."* The **data** is `src/seeds/builtinTemplates.json` and its
 * shape is validated where it is parsed (`app/seedTemplates.ts`); what belongs here is the one
 * decision — which rows are missing.
 *
 * Idempotent, which matters more than it looks: this runs at every launch, and PRE-6 (U-16) will
 * put user templates in the same table. Filtering by id rather than clearing and re-inserting means
 * a re-run cannot destroy a user's own template, and cannot renumber the ids the library renders by.
 */
export function handleInstallSeedTemplates(
  cmd: InstallSeedTemplatesCommand,
  snap: InstallSeedTemplatesSnapshot,
): Decision<SetupOp> {
  const present = new Set(snap.existingIds);
  const missing = cmd.templates.filter((row) => !present.has(row.id));

  if (missing.length === 0) return ok({ ops: [], log: [], post: [] });

  return ok({
    ops: missing.map((row) => ({ t: 'insertTemplate', row }) as const),
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: null,
        msg: `seeded template library installed: ${String(missing.length)} of ${String(cmd.templates.length)} groups`,
        data: { installed: missing.map((row) => row.id) },
      },
    ],
    post: [],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-4 — Player, created on demand
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRE-4 / FILE-2 — binds a shirt number to a `Player` row, creating it on demand.
 *
 * **Idempotent by `[teamId+number]`, which is the whole point.** FILE-2 requires player rows to
 * persist across matches, so resolving "#9" in November must reach the row created in September
 * rather than making a second one. The snapshot is read inside the transaction, so the three
 * outcomes are decided against committed state:
 *
 * | `existing` | Outcome |
 * |---|---|
 * | none | insert the caller's row |
 * | the caller's own id | **no ops** — a re-dispatch of the same resolution is a no-op, not a duplicate |
 * | a different id | reject `ERR_PLAYER_NUMBER_TAKEN`, carrying the id the caller should bind to |
 *
 * ENV-11 is enforced by the schema rather than here: `PlayerRow` has no name or appearance field, so
 * there is nothing for this handler to refuse.
 */
export function handleCreatePlayer(
  cmd: CreatePlayerCommand,
  snap: CreatePlayerSnapshot,
): Decision<SetupOp> {
  const { row } = cmd;

  if (
    !Number.isInteger(row.number) ||
    row.number < config.SHIRT_NUMBER_MIN ||
    row.number > config.SHIRT_NUMBER_MAX
  ) {
    return reject(
      MODULE,
      null,
      'ERR_SHIRT_NUMBER_RANGE',
      `A shirt number must be between ${String(config.SHIRT_NUMBER_MIN)} and ${String(config.SHIRT_NUMBER_MAX)}.`,
      { teamId: row.teamId, number: row.number },
    );
  }

  if (snap.existing !== undefined) {
    if (snap.existing.id === row.id) {
      return ok({ ops: [], log: [], post: [] });
    }
    return reject(
      MODULE,
      null,
      'ERR_PLAYER_NUMBER_TAKEN',
      'That shirt number is already on file for this team.',
      { teamId: row.teamId, number: row.number, existingId: snap.existing.id },
    );
  }

  return ok({
    ops: [{ t: 'insertPlayer', row }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: null,
        msg: 'player created on demand',
        data: { id: row.id, teamId: row.teamId, number: row.number },
      },
    ],
    post: [],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-1 / SES-5 — match setup
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRE-1 / SES-5 — edits the setup of a match that has not kicked off.
 *
 * `MatchSetupPatch`'s key union already makes a state change, an anchor write and a `capturePhase`
 * change unspellable. What this adds is the three value rules the screen's controls imply:
 *
 * - **SES-5** — `halfLengthMin` inside `[HALF_LENGTH_MIN_MIN, HALF_LENGTH_MIN_MAX]`. §2.2 guards it
 *   again at Kickoff, but a value that only fails at Kickoff fails at the worst possible moment.
 * - **PRE-1 / PRE-8** — `formationGuess` must be offerable by **this match's format** (§6.0a). While
 *   no format is set nothing is offerable, so every value is refused; once one is set, the other two
 *   lists are refused with it. *"picker over a fixed shape list … no drawing surface"*; a free-form
 *   value here is the beginning of one.
 * - **§3.1** — `date` is a `YYYY-MM-DD` calendar date, because FILE-3's history ordering and PRE-5's
 *   recency compare are lexicographic on it.
 *
 * ── PRE-8's reconciliation, and why it rides on the same op ──────────────────
 *
 * The format control and the shape picker are both live for the whole of Pre-Match, so setting a
 * format *after* a shape was chosen is reachable. §6.0a: changing `format` **clears
 * `formationGuess` if and only if the stored value is not offerable by the new format**, and does it
 * in the same transaction. That is why the clear is folded into this handler's single
 * `patchMatchSetup` op rather than dispatched as a second command — two commands would leave a
 * committed window in which the row pairs a `9v9` format with a `4-2-3-1` guess, which is exactly
 * the pairing `MatchSchema`'s cross-field refinement refuses.
 *
 * The state guard is `preMatch` only. Half length after kickoff would silently re-derive every
 * second-half minute (SES-4), which is SES-8's job in triage and nothing else's.
 */
export function handleUpdateMatchSetup(
  cmd: UpdateMatchSetupCommand,
  snap: UpdateMatchSetupSnapshot,
): Decision<SetupOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (match.state !== MATCH_STATE.preMatch) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_SETUP_NOT_PERMITTED',
      'This match has already kicked off, so its setup can no longer be changed.',
      { state: match.state },
    );
  }

  const { patch } = cmd;

  if (patch.halfLengthMin !== undefined) {
    const minutes = patch.halfLengthMin;
    if (
      !Number.isInteger(minutes) ||
      minutes < config.HALF_LENGTH_MIN_MIN ||
      minutes > config.HALF_LENGTH_MIN_MAX
    ) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_HALF_LENGTH_RANGE',
        `A half is between ${String(config.HALF_LENGTH_MIN_MIN)} and ${String(config.HALF_LENGTH_MIN_MAX)} minutes.`,
        { halfLengthMin: minutes },
      );
    }
  }

  // PRE-8 — the format this patch leaves the row in. A guess arriving in the *same* patch as a
  // format is checked against the incoming format, not the stored one, so setting both at once is
  // legal and does not depend on which key the screen wrote first.
  const format = patch.format ?? match.format;

  if (
    patch.formationGuess !== undefined &&
    patch.formationGuess !== null &&
    !isFormationFor(format, patch.formationGuess)
  ) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_FORMATION_UNKNOWN',
      format === null
        ? 'Choose the match format first — which shapes are on offer depends on it.'
        : `Pick a formation from the ${format} list — Touchline does not draw shapes.`,
      { formationGuess: patch.formationGuess, format },
    );
  }

  if (patch.date !== undefined && !isCalendarDate(patch.date)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_MATCH_DATE_INVALID',
      'A match date looks like 2026-08-02.',
      { date: patch.date },
    );
  }

  if (patch.teamId !== undefined && !snap.teamExists) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TEAM_NOT_FOUND',
      'That opponent no longer exists.',
      { teamId: patch.teamId },
    );
  }

  /**
   * PRE-8 — everything a format change carries with it, in **this one op**.
   *
   * Two derived writes, both conditional on the caller not having said otherwise in the same patch:
   *
   * - **`formationGuess`** is cleared when the new format cannot field it. `other` is in every list
   *   and therefore survives; `3-4-3` does not survive a switch to 9v9.
   * - **`halfLengthMin`** takes the new format's SES-5 default while it is still a default (F-41).
   *   A value the user set themselves is left alone — see `reconcileHalfLength`.
   *
   * Both ride on the single `patchMatchSetup` op rather than becoming further commands, so no
   * committed state ever pairs a `9v9` format with a `4-2-3-1` guess or with an 11v11 half.
   */
  let applied: MatchSetupPatch = patch;
  let clearedFormationGuess: string | null = null;
  let followedHalfLength: number | null = null;

  if (patch.format !== undefined) {
    if (patch.formationGuess === undefined) {
      const nextGuess = reconcileFormationGuess(patch.format, match.formationGuess);
      if (nextGuess !== match.formationGuess) {
        clearedFormationGuess = match.formationGuess;
        applied = { ...applied, formationGuess: nextGuess };
      }
    }
    if (patch.halfLengthMin === undefined) {
      const nextHalf = reconcileHalfLength(match.format, patch.format, match.halfLengthMin);
      if (nextHalf !== null) {
        followedHalfLength = nextHalf;
        applied = { ...applied, halfLengthMin: nextHalf };
      }
    }
  }

  const derived = [
    clearedFormationGuess === null
      ? null
      : `the formation guess was cleared because ${patch.format ?? '?'} does not offer it`,
    followedHalfLength === null
      ? null
      : `the half length followed ${patch.format ?? '?'} to ${String(followedHalfLength)}`,
  ].filter((note): note is string => note !== null);

  return ok({
    ops: [{ t: 'patchMatchSetup', id: cmd.matchId, patch: applied }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg:
          derived.length === 0
            ? 'match setup updated'
            : `match setup updated; ${derived.join('; ')}`,
        data: {
          fields: Object.keys(applied).sort(),
          ...(clearedFormationGuess === null ? {} : { clearedFormationGuess }),
          ...(followedHalfLength === null ? {} : { followedHalfLength }),
        },
      },
    ],
    post: [],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-7 — the exit
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every property a freshly-created hypothesis must have, checked as one.
 *
 * The rows are pre-built by the caller (§5.1's discipline, applied off the tap path), so this is the
 * boundary where "the caller built it correctly" stops being an assumption. `boardPosition: null` is
 * the load-bearing one: positions are assigned by the ops this handler emits, from
 * `planLockPositions`, and a row arriving with a position already on it would be a second
 * assignment path (INV-4) that no later check could distinguish from the first.
 *
 * **Exported for DISC-5** (build unit U-8), which creates a row on the same terms mid-Discovery from
 * the same `toHypothesisRow` constructor. One operation, one symbol: a second copy would be a second
 * place for `boardPosition: null` to stop being checked, and the two could not diverge — "freshly
 * created" means the same six things whichever surface built the row.
 */
export function freshHypothesisProblem(row: HypothesisRow, matchId: string): string | null {
  if (row.matchId !== matchId) return 'belongs to a different match';
  if (row.boardPosition !== null) return 'already carries a board position';
  if (row.cachedCount !== 0) return 'already carries a count';
  if (row.statusOverride !== null) return 'already carries a status override';
  if (row.status !== 'tracking') return 'is not tracking';
  if (row.lastIncrementTs !== null) return 'already carries an increment timestamp';
  return null;
}

/**
 * PRE-7 — creates the selected candidates and, for "Start locked", assigns their positions.
 *
 * See {@link StartCaptureCommand} for the two exits and why they are one command. The negative this
 * handler is responsible for is the one §6.0 names: **"Start in Discovery" assigns no
 * `boardPosition`**, so `positionedHypothesisIds` must be empty in that mode and a later LOCK-4
 * confirm is never a second assignment against INV-4.
 *
 * ── PRE-8's clamp, and why there is exactly one of it ────────────────────────
 *
 * PRE-8 makes the format required to leave Pre-Match. This command is the **single routing point**:
 * it is behind *both* PRE-7 exits, and `Match.state` stays `preMatch` until Kickoff is held on a
 * capture surface — so there is no path from Pre-Match to an in-match state that does not pass
 * through here. §17's scope rule prefers one clamp where all callers route over per-call-site
 * re-assertion, so **no second guard is added at Kickoff**: a second copy is a second thing to get
 * wrong.
 *
 * The disabled exits on the screen are the **courtesy**, not the mechanism. A disabled button is a
 * claim about a render; this rejection is the fact, and it is what the verification asserts.
 */
export function handleStartCapture(
  cmd: StartCaptureCommand,
  snap: StartCaptureSnapshot,
): Decision<BoardOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (match.state !== MATCH_STATE.preMatch) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_SETUP_NOT_PERMITTED',
      'This match has already kicked off; its board was set at Pre-Match.',
      { state: match.state },
    );
  }

  // PRE-8's one clamp. Checked before anything about the board, because the format is a fact about
  // the match rather than about the selection: a refusal here must read the same whether the user
  // selected eleven candidates or none.
  if (match.format === null) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_MATCH_FORMAT_REQUIRED',
      'Choose the match format first — 7v7, 9v9 or 11v11.',
      { mode: cmd.mode },
    );
  }

  const ids = new Set<string>();
  for (const row of cmd.hypotheses) {
    if (row.text.trim() === '') {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_HYPOTHESIS_TEXT_REQUIRED',
        'One of the selected hypotheses has no text.',
        { id: row.id },
      );
    }
    const problem = freshHypothesisProblem(row, cmd.matchId);
    if (problem !== null) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'The board could not be created because one of its rows was malformed.',
        { id: row.id, problem },
      );
    }
    if (ids.has(row.id)) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'The same hypothesis was selected twice.',
        { id: row.id },
      );
    }
    ids.add(row.id);
  }

  const inserts = cmd.hypotheses.map((row) => ({ t: 'insertHypothesis', row }) as const);

  if (cmd.mode === 'discovery') {
    // §6.0: *"the selected candidates are created **list-only**, with `boardPosition = null`. No
    // position is assigned."* An id list here would mean the caller believed otherwise, and the
    // consequence — a LOCK-4 confirm later assigning positions a second time — is exactly what
    // INV-4 forbids and what no downstream check could detect.
    if (cmd.positionedHypothesisIds.length > 0) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'Starting in Discovery assigns no board positions.',
        { positioned: cmd.positionedHypothesisIds.length },
      );
    }
    return ok({
      ops: inserts,
      log: [
        {
          module: MODULE,
          level: 'info',
          matchId: cmd.matchId,
          msg: `pre-match board created list-only with ${String(inserts.length)} hypotheses`,
          data: { mode: cmd.mode, created: inserts.length },
        },
      ],
      // Deliberately empty (§1.1: "empty is a decision"). No op here writes a `boardPosition`, so
      // `countPositioned` cannot move and INV-5 has nothing to assert against.
      post: [],
    });
  }

  if (!canLock(match.state, match.capturePhase)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_LOCK_NOT_PERMITTED',
      'This board is already locked; positions are assigned exactly once (INV-4).',
      { state: match.state, capturePhase: match.capturePhase },
    );
  }

  const known = new Set([...ids, ...snap.hypotheses.map((row) => row.id)]);
  const foreign = cmd.positionedHypothesisIds.filter((id) => !known.has(id));
  if (foreign.length > 0) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_NOT_FOUND',
      'The board selection names hypotheses that are not in this match.',
      { missing: foreign },
    );
  }

  const plan = planLockPositions(cmd.positionedHypothesisIds, [
    ...snap.hypotheses,
    ...cmd.hypotheses,
  ]);
  if (!plan.ok) return plan as DomainResult<never>;

  return ok({
    ops: [
      ...inserts,
      { t: 'setBoardPositions', matchId: cmd.matchId, writes: plan.value },
      { t: 'confirmLock', id: cmd.matchId, lockTs: snap.now },
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: `pre-match board started locked with ${String(plan.value.length)} of ${String(inserts.length)} positioned`,
        data: { mode: cmd.mode, created: inserts.length, positioned: plan.value.length },
      },
    ],
    // INV-5 / LIVE-8, for the same reason `handleConfirmLock` declares it: the repo aborts above the
    // cap already, and asserting it at the bus covers a future op that reaches a position another way.
    post: [{ t: 'boardCap', matchId: cmd.matchId }],
  });
}
